import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, describeError } from "@/lib/api";
import { StatusBadge } from "@/components/common/StatusBadge";
import { LoadingState } from "@/components/common/EmptyState";
import CaseFormDialog from "@/pages/CaseFormDialog";
import HearingDialog from "@/pages/HearingDialog";
import HearingCompleteDialog from "@/pages/HearingCompleteDialog";
import AdjournDialog from "@/pages/AdjournDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { fmtDate } from "@/lib/helpers";
import DocumentList from "@/components/documents/DocumentList";
import DocumentUploadDialog from "@/components/documents/DocumentUploadDialog";
import { toast } from "sonner";
import {
  ArrowLeft, Pencil, Plus, Gavel, Clock, FileText, ListChecks, StickyNote, MessageSquare, GitBranch, Paperclip, Upload, CheckCircle2, XCircle,
} from "lucide-react";

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm text-navy">{value || "—"}</p>
    </div>
  );
}

export default function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [hearingOpen, setHearingOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [update, setUpdate] = useState("");
  const [completeTarget, setCompleteTarget] = useState(null);
  const [editHearing, setEditHearing] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [adjournTarget, setAdjournTarget] = useState(null);
  const [updateTitle, setUpdateTitle] = useState("");

  const cancelHearing = async () => {
    try {
      await api.post(`/hearings/${cancelTarget.id}/cancel`);
      toast.success("Hearing cancelled");
      load();
    } catch (err) { toast.error(describeError(err, "Could not cancel the hearing.")); }
    finally { setCancelTarget(null); }
  };

  const loadDocs = useCallback(async () => {
    const { data } = await api.get("/documents", { params: { case_id: id } });
    setDocuments(data);
  }, [id]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/cases/${id}`);
      setData(data);
      loadDocs();
    } catch { navigate("/cases"); }
  }, [id, navigate, loadDocs]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <LoadingState />;
  const { case: c, hearings, timeline, tasks, client_updates } = data;

  const addUpdate = async () => {
    if (!update.trim()) return;
    try {
      await api.post(`/cases/${id}/client-updates`, { title: updateTitle || "Case Update", message: update });
      setUpdate(""); setUpdateTitle("");
      toast.success("Client update posted");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="case-detail-page">
      <button onClick={() => navigate("/cases")} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-navy">
        <ArrowLeft className="h-4 w-4" /> Back to cases
      </button>

      <div className="mb-6 flex flex-col gap-4 rounded-lg border border-border bg-white p-6 shadow-sm sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-3xl tracking-tight text-navy">{c.title}</h1>
            <StatusBadge status={c.status} />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{c.case_number} · {c.court || "No court"}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setHearingOpen(true)} data-testid="add-hearing-btn"><Plus className="mr-1.5 h-4 w-4" /> Hearing</Button>
          <Button onClick={() => setEditOpen(true)} data-testid="edit-case-btn" className="bg-navy text-white hover:bg-navy-light"><Pencil className="mr-1.5 h-4 w-4" /> Edit</Button>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="mb-4 flex w-full flex-wrap justify-start gap-1 bg-secondary/60">
          <TabsTrigger value="overview" data-testid="tab-overview"><FileText className="mr-1.5 h-3.5 w-3.5" />Overview</TabsTrigger>
          <TabsTrigger value="hearings" data-testid="tab-hearings"><Gavel className="mr-1.5 h-3.5 w-3.5" />Hearings ({hearings.length})</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline"><GitBranch className="mr-1.5 h-3.5 w-3.5" />Timeline</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks"><ListChecks className="mr-1.5 h-3.5 w-3.5" />Tasks ({tasks.length})</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents"><Paperclip className="mr-1.5 h-3.5 w-3.5" />Documents ({documents.length})</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes"><StickyNote className="mr-1.5 h-3.5 w-3.5" />Notes</TabsTrigger>
          <TabsTrigger value="updates" data-testid="tab-updates"><MessageSquare className="mr-1.5 h-3.5 w-3.5" />Client Updates</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Card className="border-border p-6 shadow-sm">
            <div className="grid grid-cols-2 gap-5 md:grid-cols-3">
              <Info label="Case Type" value={c.case_type} />
              <Info label="Category" value={c.category} />
              <Info label="Court" value={c.court} />
              <Info label="Court Location" value={c.court_location} />
              <Info label="Filing Date" value={fmtDate(c.filing_date)} />
              <Info label="Client" value={c.client_name} />
              <Info label="Advocate" value={c.advocate_name} />
              <Info label="Opposite Party" value={c.opposite_party_name} />
              <Info label="Legal Sections" value={c.legal_sections} />
              <Info label="Next Hearing" value={fmtDate(c.next_hearing_date)} />
            </div>
            {(c.description || c.important_facts || c.remarks) && (
              <div className="mt-6 space-y-4 border-t border-border pt-6">
                {c.description && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Description</p><p className="mt-1 text-sm leading-relaxed text-navy">{c.description}</p></div>}
                {c.important_facts && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Important Facts</p><p className="mt-1 text-sm leading-relaxed text-navy">{c.important_facts}</p></div>}
                {c.remarks && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Remarks</p><p className="mt-1 text-sm leading-relaxed text-navy">{c.remarks}</p></div>}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="hearings">
          <Card className="divide-y divide-border border-border shadow-sm">
            {hearings.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No hearings recorded.</div>}
            {hearings.map((h) => (
              <div key={h.id} className="flex items-start gap-4 p-4">
                <div className="flex w-20 shrink-0 flex-col items-center rounded-md bg-navy/5 py-2">
                  <Clock className="h-3.5 w-3.5 text-navy" />
                  <span className="mt-1 text-xs font-semibold text-navy">{fmtDate(h.date)}</span>
                  <span className="text-xs text-muted-foreground">{h.time}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2"><p className="font-medium text-navy">{h.purpose || "Hearing"}</p><StatusBadge status={h.status} /></div>
                  <p className="text-xs text-muted-foreground">{h.court} · {h.court_room}</p>
                  {h.order_result && <p className="mt-1 text-sm text-navy">Order: {h.order_result}</p>}
                  {h.what_happened && <p className="text-sm text-muted-foreground">{h.what_happened}</p>}
                </div>
                {h.status === "scheduled" && (
                  <div className="flex shrink-0 flex-col gap-1.5 sm:flex-row">
                    <Button size="sm" onClick={() => setCompleteTarget(h)} data-testid={`hearing-complete-btn-${h.id}`} className="bg-emerald-600 text-white hover:bg-emerald-700"><CheckCircle2 className="mr-1 h-3.5 w-3.5" />Complete</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditHearing(h)} data-testid={`hearing-edit-btn-${h.id}`}><Pencil className="mr-1 h-3.5 w-3.5" />Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => setAdjournTarget(h)} data-testid={`hearing-adjourn-btn-${h.id}`} className="text-amber-700 hover:bg-amber-50"><Clock className="mr-1 h-3.5 w-3.5" />Adjourn</Button>
                    <Button size="sm" variant="outline" onClick={() => setCancelTarget(h)} data-testid={`hearing-cancel-btn-${h.id}`} className="text-rose-600 hover:bg-rose-50"><XCircle className="mr-1 h-3.5 w-3.5" />Cancel</Button>
                  </div>
                )}
              </div>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="timeline">
          <Card className="border-border p-6 shadow-sm">
            <div className="relative space-y-6 pl-6">
              <div className="absolute left-[7px] top-1 h-full w-px bg-border" />
              {timeline.map((t) => (
                <div key={t.id} className="relative">
                  <div className="absolute -left-6 top-1 h-3.5 w-3.5 rounded-full border-2 border-gold bg-white" />
                  <p className="text-sm font-medium text-navy">{t.event}</p>
                  <p className="text-xs text-muted-foreground">{t.detail}</p>
                  <p className="text-xs text-muted-foreground/70">{fmtDate(t.created_at)} · {t.actor}</p>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="tasks">
          <Card className="divide-y divide-border border-border shadow-sm">
            {tasks.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No tasks linked to this case.</div>}
            {tasks.map((t) => (
              <div key={t.id} className="flex items-center justify-between p-4">
                <div><p className="text-sm font-medium text-navy">{t.title}</p><p className="text-xs text-muted-foreground">Due {fmtDate(t.due_date)}</p></div>
                <StatusBadge status={t.status} />
              </div>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <div className="mb-3 flex justify-end">
            <Button onClick={() => setUploadOpen(true)} data-testid="case-upload-doc-btn" className="bg-navy text-white hover:bg-navy-light"><Upload className="mr-1.5 h-4 w-4" /> Upload Document</Button>
          </div>
          <DocumentList documents={documents} onChanged={loadDocs} />
        </TabsContent>

        <TabsContent value="notes">
          <Card className="border-border p-6 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Private Advocate Notes</p>
            <p className="mt-2 text-sm leading-relaxed text-navy">{c.remarks || c.hearing_notes || "No private notes for this case."}</p>
            <p className="mt-4 rounded-md bg-secondary/60 p-3 text-xs text-muted-foreground">These notes are never visible to clients.</p>
          </Card>
        </TabsContent>

        <TabsContent value="updates">
          <Card className="border-border p-6 shadow-sm">
            <div className="mb-4 flex flex-col gap-2">
              <Input data-testid="client-update-title" value={updateTitle} onChange={(e) => setUpdateTitle(e.target.value)} placeholder="Update title (e.g. Hearing outcome)" />
              <div className="flex flex-col gap-2 sm:flex-row">
                <Textarea data-testid="client-update-input" value={update} onChange={(e) => setUpdate(e.target.value)} placeholder="Write an update visible to the client…" rows={2} className="flex-1" />
                <Button onClick={addUpdate} data-testid="post-update-btn" className="bg-navy text-white hover:bg-navy-light">Share with Client</Button>
              </div>
            </div>
            <div className="space-y-3">
              {client_updates.length === 0 && <p className="text-sm text-muted-foreground">No client-visible updates yet.</p>}
              {client_updates.map((u) => (
                <div key={u.id} className="rounded-md border border-border bg-secondary/30 p-3">
                  <p className="text-sm text-navy">{u.message}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{u.created_by} · {fmtDate(u.created_at)}</p>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <CaseFormDialog open={editOpen} onOpenChange={setEditOpen} initial={c} onSaved={load} />
      <HearingDialog open={hearingOpen} onOpenChange={setHearingOpen} caseId={id} onSaved={load} />
      <HearingDialog open={!!editHearing} onOpenChange={(o) => !o && setEditHearing(null)} caseId={id} initial={editHearing} onSaved={load} />
      <HearingCompleteDialog open={!!completeTarget} onOpenChange={(o) => !o && setCompleteTarget(null)} hearing={completeTarget} onSaved={load} />
      <AdjournDialog open={!!adjournTarget} onOpenChange={(o) => !o && setAdjournTarget(null)} hearing={adjournTarget} onSaved={load} />
      <DocumentUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} caseId={id} onUploaded={loadDocs} />

      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this hearing?</AlertDialogTitle>
            <AlertDialogDescription>
              The hearing on {cancelTarget ? fmtDate(cancelTarget.date) : ""} will be marked as Cancelled. The record is preserved in the case history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="hearing-cancel-dismiss">Keep hearing</AlertDialogCancel>
            <AlertDialogAction onClick={cancelHearing} data-testid="hearing-cancel-confirm" className="bg-rose-600 hover:bg-rose-700">Cancel hearing</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
